import { AddressTypes, SaveLocationOption } from 'common/constants'
import { ValidationErrorMessage } from 'components/Forms'
import { StyledModal } from 'components/Modal.styled'
import { Form, Formik } from 'formik'
import { DropdownList } from 'layouts/DropdownList'
import { useEffect, useMemo, useState } from 'react'
import { Button, Col, FormControl as Input, Modal, Row } from 'react-bootstrap'
import { DropDownData, LocationDropDown } from 'types/Grounding/GroundingResult'
import {
  addressValidateSchema,
  IAddressForm,
  initialAddressValue,
  SRPAddressValidateSchema
} from 'types/Grounding/groundingTypes'
import { StyledAddressModal } from './AddressModal.styled'
import { DeliveryInformation } from 'types/vehicleTypes'
import { IDropdownData, IOption } from 'types/baseTypes'
import { getStatesByCountryID } from 'apis/vehicleApis'
import { CustomRadioButton } from 'components/DeliveryChargeCard/CustomRadioButton'
import { useBiddingSidePanel } from 'store/useBiddingSidePanelStore'

interface IAddressProps {
  deliveryInformation?: DeliveryInformation
  defaultAddressDetail: LocationDropDown
  stateList?: DropDownData[]
  show: boolean
  isEditAddress?: boolean
  isModal?: boolean
  addressTypeId?: number
  events: {
    onClose: any
    handleSaveAddress: any
    setIsSaveAddressToBuyer: any
  }
}

export const AddressModal = ({
  deliveryInformation,
  defaultAddressDetail,
  isEditAddress = false,
  stateList = [],
  show,
  isModal = true,
  addressTypeId = AddressTypes.VEHICLE_PICKUP_LOCATION,
  events
}: IAddressProps) => {
  const { setDeliveryInformation } = useBiddingSidePanel((state) => ({
    setDeliveryInformation: state.setDeliveryInformation
  }))
  const [isSubmit, setIsSubmit] = useState(false)
  const [addressDetail] = useState<LocationDropDown>(defaultAddressDetail)
  const [saveOptionSelected, setSaveOptionSelected] = useState(0)
  const [initLocationData, setInitLocationData] = useState<IAddressForm>(initialAddressValue)
  const [isLoading, setIsLoading] = useState(true)
  const [dynamicStates, setDynamicStates] = useState<IDropdownData[]>([])
  const [isLoadingStates, setIsLoadingStates] = useState(false)
  const [hasCountryBeenSelected, setHasCountryBeenSelected] = useState(false)

  useEffect(() => {
    if (defaultAddressDetail != null) {
      setInitLocationData({
        address: defaultAddressDetail.Address1,
        address2: defaultAddressDetail.Address2,
        city: defaultAddressDetail.City,
        zip: defaultAddressDetail.Zip,
        stateCode: defaultAddressDetail?.StateCode,
        countryCode: defaultAddressDetail?.CountryCode
      })
    } else {
      setInitLocationData({
        ...initialAddressValue,
        countryCode: deliveryInformation?.DefaultCountry?.toString() || '-1'
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddressDetail, deliveryInformation?.DefaultCountry])

  useEffect(() => {
    const fetchStates = async () => {
      const result = await getStatesByCountryID(parseInt(deliveryInformation?.DefaultCountry?.toString() || '-1'))
      if (result) {
        setDeliveryInformation({
          ...deliveryInformation,
          States: result as IDropdownData[]
        })
      }
    }
    fetchStates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (deliveryInformation?.Countries || deliveryInformation == null) {
      setIsLoading(false)
    }
  }, [deliveryInformation])

  const handleSaveAddress = (values: IAddressForm) => {
    if (saveOptionSelected === 0 && !isEditAddress) {
      return
    }

    events.handleSaveAddress({
      AddressLineOne: values.address,
      AddressLineTwo: values.address2,
      Town: values.city,
      StateId: deliveryInformation
        ? values.stateCode
        : stateList?.find((x) => x.FieldDescription === values.stateCode)?.ID ?? 0,
      PostCode: values.zip,
      AddressId:
        isEditAddress && defaultAddressDetail?.AddressTypeID !== AddressTypes.HEAD_OFFICE
          ? defaultAddressDetail?.ID
          : 0,
      CountryId: values.countryCode !== '-1' ? Number(values.countryCode) : null,
      AddressTypeId:
        isEditAddress && defaultAddressDetail?.AddressTypeID !== AddressTypes.HEAD_OFFICE
          ? defaultAddressDetail?.AddressTypeID
          : addressTypeId
    })
  }

  const countryList: IOption<IDropdownData>[] = useMemo(() => {
    return (
      deliveryInformation?.Countries?.map((country) => ({
        item: country,
        label: country.Description,
        value: country.ID
      })) || []
    )
  }, [deliveryInformation?.Countries])

  const dynamicStateList: IOption<IDropdownData>[] = useMemo(() => {
    // Use dynamic states if a country has been selected, otherwise use deliveryInformation.States
    const statesToUse = hasCountryBeenSelected ? dynamicStates : deliveryInformation?.States || []
    return statesToUse.map((state) => ({
      item: state,
      label: state.Description,
      value: state.ID
    }))
  }, [dynamicStates, deliveryInformation?.States, hasCountryBeenSelected])

  const handleCountryChange = async (countryId: string, formikProps: any) => {
    formikProps.setFieldValue('countryCode', countryId)
    formikProps.setFieldValue('stateCode', '-1')

    if (countryId === '-1') {
      setDynamicStates([])
      setHasCountryBeenSelected(false)
      return
    }

    setHasCountryBeenSelected(true)
    try {
      setIsLoadingStates(true)
      const states = await getStatesByCountryID(parseInt(countryId))
      setDynamicStates(states)
      const updatedDeliveryInfo = (prev: DeliveryInformation | undefined) => {
        if (!prev) return prev
        return {
          ...prev,
          States: states as IDropdownData[]
        }
      }
      setDeliveryInformation(updatedDeliveryInfo(deliveryInformation))
    } catch (error) {
      console.error('Error fetching states:', error)
      setDynamicStates([])
    } finally {
      setIsLoadingStates(false)
    }
  }

  const handleSaveOptionChange = (e: any) => {
    setSaveOptionSelected(Number(e.target.id))
    if (Number(e.target.id) === SaveLocationOption.SAVE_TO_BUYER) {
      events.setIsSaveAddressToBuyer(true)
    } else {
      events.setIsSaveAddressToBuyer(false)
    }
  }

  // If not showing and is modal, return null
  if (!show && isModal) {
    return null
  }

  const renderAddressForm = (props: any) => (
    <Form>
      <div>
        <label className="required-field">Address</label>
        <Input
          name="address"
          defaultValue={addressDetail?.Address1}
          isInvalid={(isSubmit || props.touched?.address) && !!props.errors?.address}
          onChange={props.handleChange}
          onBlur={props.handleBlur}
          value={props.values?.address}
        />
        <ValidationErrorMessage>
          {(isSubmit || props.touched?.address) && !!props.errors?.address && props.errors?.address}
        </ValidationErrorMessage>
      </div>
      <div style={{ marginTop: '8px' }}>
        <label>Address 2</label>
        <Input
          name="address2"
          defaultValue={addressDetail?.Address2}
          onChange={props.handleChange}
          onBlur={props.handleBlur}
          value={props.values?.address2}
        />
        <p className="error-label"></p>
      </div>
      <Row style={{ marginTop: '8px' }}>
        <Col sm={6}>
          <div>
            <label className="required-field">City</label>
            <Input
              name="city"
              defaultValue={addressDetail?.City}
              isInvalid={(isSubmit || props.touched?.city) && !!props.errors?.city}
              onChange={props.handleChange}
              onBlur={props.handleBlur}
              value={props.values?.city}
            />
            <ValidationErrorMessage>
              {(isSubmit || props.touched?.city) && !!props.errors?.city && props.errors?.city}
            </ValidationErrorMessage>
          </div>
        </Col>
        {deliveryInformation ? (
          <Col sm={6}>
            <div>
              <label className="required-field">Country</label>
              <DropdownList
                defaultValue={deliveryInformation?.DefaultCountry?.toString() || '-1'}
                items={
                  isLoading
                    ? [{ id: '-1', value: 'Loading...' }]
                    : [
                        { id: '-1', value: 'Choose a Country' },
                        ...countryList?.map((country) => ({
                          id: country.value.toString(),
                          value: country.label
                        }))
                      ]
                }
                onChange={(selectedId) => {
                  handleCountryChange(selectedId, props)
                  props.setFieldTouched('countryCode', true)
                }}
              />
              <ValidationErrorMessage>
                {(isSubmit || props.touched?.countryCode) && !!props.errors?.countryCode && props.errors?.countryCode}
              </ValidationErrorMessage>
            </div>
          </Col>
        ) : (
          <Col sm={6}>
            <div>
              <label className="required-field">State</label>
              <DropdownList
                defaultValue={addressDetail ? addressDetail.StateCode : '-1'}
                items={[
                  { id: '-1', value: 'Choose...' },
                  ...stateList?.map((item) => ({
                    id: item?.FieldDescription,
                    value: item?.Description
                  }))
                ]}
                onChange={(id) => {
                  props.setFieldValue('stateCode', id)
                  props.setFieldValue('stateCode', id, false)
                  setTimeout(() => {
                    props.setFieldTouched('stateCode', true)
                  }, 10)
                }}
              />
              <ValidationErrorMessage>
                {(isSubmit || props.touched?.stateCode) && !!props.errors?.stateCode && props.errors?.stateCode}
              </ValidationErrorMessage>
            </div>
          </Col>
        )}
      </Row>
      <Row style={{ marginTop: '8px' }}>
        {deliveryInformation && (
          <Col sm={6}>
            <div>
              <label className="required-field">State</label>
              <DropdownList
                defaultValue={dynamicStateList?.[0]?.value || '-1'}
                items={
                  isLoadingStates
                    ? [{ id: '-1', value: 'Loading states...' }]
                    : dynamicStateList.length === 0
                    ? [{ id: '-1', value: 'No states available' }]
                    : [
                        { id: '-1', value: 'Choose a State' },
                        ...dynamicStateList?.map((state) => ({
                          id: state.value.toString(),
                          value: state.label
                        }))
                      ]
                }
                onChange={(selectedId) => {
                  props.setFieldValue('stateCode', selectedId)
                  props.setFieldValue('stateCode', selectedId, false)
                  setTimeout(() => {
                    props.setFieldTouched('stateCode', true)
                  }, 10)
                }}
              />
              <ValidationErrorMessage>
                {(isSubmit || props.touched?.stateCode) && !!props.errors?.stateCode && props.errors?.stateCode}
              </ValidationErrorMessage>
            </div>
          </Col>
        )}
        <Col sm={deliveryInformation ? 6 : 6}>
          <div>
            <label className="required-field">Zip</label>
            <Input
              name="zip"
              maxLength={deliveryInformation ? 10 : 5}
              defaultValue={addressDetail?.Zip}
              isInvalid={(isSubmit || props.touched?.zip) && !!props.errors?.zip}
              onChange={props.handleChange}
              onBlur={props.handleBlur}
              value={props.values?.zip}
            />
            <ValidationErrorMessage>
              {(isSubmit || props.touched?.zip) && !!props.errors?.zip && props.errors.zip}
            </ValidationErrorMessage>
          </div>
        </Col>
      </Row>

      {!isEditAddress && (
        <Row className="save-option">
          <Col sm={12}>
            <CustomRadioButton
              id="1"
              name="saveOption"
              value="1"
              checked={saveOptionSelected === 1}
              onChange={(e) => handleSaveOptionChange(e)}
            />
            <label htmlFor="1">Add to My Locations</label>
          </Col>
          <Col sm={12}>
            <CustomRadioButton
              id="2"
              name="saveOption"
              value="2"
              checked={saveOptionSelected === 2}
              onChange={(e) => handleSaveOptionChange(e)}
            />
            <label htmlFor="2">Do not add to My Locations</label>
          </Col>
          <Col sm={12}>
            <ValidationErrorMessage>
              {isSubmit && saveOptionSelected === 0 && 'Please select an option'}
            </ValidationErrorMessage>
          </Col>
        </Row>
      )}

      <div className={isModal ? 'btn-group' : 'button-group'}>
        <Button
          type="button"
          variant="primary"
          style={!isModal ? { marginRight: '10px' } : {}}
          onClick={() => {
            setIsSubmit(true)
            props.handleSubmit()
          }}
        >
          Save
        </Button>
        <Button
          type="button"
          className={isModal ? 'btn-secondary' : ''}
          variant={isModal ? undefined : 'light'}
          onClick={() => {
            events.onClose()
            setIsSubmit(false)
          }}
        >
          Cancel
        </Button>
      </div>
    </Form>
  )

  // Render as modal or inline form based on isModal prop
  if (isModal) {
    return (
      <StyledModal show={show} onHide={events.onClose} centered customWidth={'550px'}>
        <StyledAddressModal>
          <Modal.Header closeButton>
            <Modal.Title>Vehicle Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Formik
              enableReinitialize
              initialValues={initLocationData}
              validationSchema={deliveryInformation ? SRPAddressValidateSchema : addressValidateSchema}
              onSubmit={(values) => handleSaveAddress(values)}
              validateOnBlur={true}
              validateOnChange={false}
            >
              {(props) => renderAddressForm(props)}
            </Formik>
          </Modal.Body>
        </StyledAddressModal>
      </StyledModal>
    )
  } else {
    return (
      <StyledAddressModal>
        <div style={{ marginTop: '20px' }}>
          <Formik
            enableReinitialize
            initialValues={initLocationData}
            validationSchema={deliveryInformation ? SRPAddressValidateSchema : addressValidateSchema}
            onSubmit={(values) => {
              if (saveOptionSelected === 0 && !isEditAddress) {
                setIsSubmit(true)
                return
              }
              setIsSubmit(false)
              handleSaveAddress(values)
            }}
            validateOnBlur={true}
            validateOnChange={false}
          >
            {(props) => renderAddressForm(props)}
          </Formik>
        </div>
      </StyledAddressModal>
    )
  }
}
